// src/services/accessRequests.ts

import { API_BASE_URL } from "@/services/index";
import type {
  AccessRequestFormData,
  AccessRequestSubmission,
  AccessRequestResponse,
} from "@/types/accessRequest";

/**
 * Submit an access request to the backend API
 * Converts frontend form data to backend API format
 */
export const submitAccessRequest = async (
  formData: AccessRequestFormData
): Promise<AccessRequestResponse> => {
  try {
    // Transform frontend format to backend format
    const submission: AccessRequestSubmission = {
      full_name: formData.fullName,
      email: formData.email,
      company: formData.company || null,
      industry: formData.industry,
      reason: formData.reason,
      phone: formData.phone || null,
      agreed_to_terms: true, // User implicitly agrees by submitting or will agree on login
    };

    const response = await fetch(`${API_BASE_URL}/access-requests`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(submission),
    });

    if (!response.ok) {
      // Handle different error status codes
      if (response.status === 429) {
        throw new Error(
          "Too many requests. Please try again in a few minutes."
        );
      }

      if (response.status === 409) {
        throw new Error(
          "An access request with this email already exists. Please check your inbox or contact support."
        );
      }

      if (response.status === 400) {
        const errorData = await response.json().catch(() => ({}));
        // Handle specific validation errors from backend
        if (errorData.message && errorData.message.includes("terms")) {
          throw new Error(
            "You must agree to the Terms and Conditions to submit your request."
          );
        }
      }

      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          "Failed to submit access request. Please try again."
      );
    }

    const data = await response.json();
    return {
      success: true,
      message: data.message || "Access request submitted successfully!",
      requestId: data.requestId || data.id,
    };
  } catch (error) {
    console.error("Error submitting access request:", error);

    // Re-throw known errors
    if (error instanceof Error) {
      throw error;
    }

    // Handle network errors
    throw new Error(
      "Unable to connect to the server. Please check your internet connection and try again."
    );
  }
};
